import { useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { TokenSuggestion } from '@components/filters/components/TokenFilterInput/types';
import { FeatureFlags, useAvContext } from '@fabric-ui/components';
import { QueryKey } from '@fabric-ui/utils';
import { gql } from 'graphql-request';
import { PermissionSetResponse, PermissionSetType, TagDto } from './ContentPermission.types';

const GET_CONTENT_PERMISSION_TOKEN_SUGGESTIONS = gql`
  query getContentPermissionTokenSuggestions {
    getContentPermissionTokenSuggestions {
      token
      operators
    }
  }
`;

type ApiTokenSuggestion = {
  token: string;
  operators: string[];
};

const mapApiTokenSuggestion = ({ token, operators }: ApiTokenSuggestion): TokenSuggestion => ({
  value: token,
  title: token,
  operator: operators[0] ?? 'equals',
});

// Inverse of serializeContentPermissionsFilter — converts the API's { in: { values: ['{token}'] } }
// back to the internal plain string format the expression builder expects.
export const deserializeContentPermissionsFilter = (filter: any, tokenSuggestions: TokenSuggestion[]): any => {
  if (!filter) return filter;
  if (filter.and) return { and: { operands: filter.and.operands.map(o => deserializeContentPermissionsFilter(o, tokenSuggestions)) } };
  if (filter.or) return { or: { operands: filter.or.operands.map(o => deserializeContentPermissionsFilter(o, tokenSuggestions)) } };
  const inTokens = new Set(tokenSuggestions.filter(t => t.operator === 'in').map(t => t.value));
  const arrayCondition = filter.expression?.arrayCondition;
  const sc = arrayCondition ? arrayCondition.underlying?.stringCondition : filter.expression?.stringCondition;
  const inValues = sc?.in?.values;
  if (!Array.isArray(inValues) || inValues.length !== 1 || !inTokens.has(inValues[0])) return filter;
  const deserializedUnderlying = { ...arrayCondition?.underlying, stringCondition: { in: inValues[0] } };
  return {
    expression: arrayCondition
      ? { arrayCondition: { underlying: deserializedUnderlying, resolution: arrayCondition.resolution } }
      : { ...filter.expression, stringCondition: { in: inValues[0] } },
  };
};

// Walks the filter tree and converts any token-driven 'in' values from a plain string
// to the array format the API expects: stringCondition.in: { values: ['{token}'] }
// For array-type fields (arrayCondition), also preserves the resolution (e.g. ANY).
export const serializeContentPermissionsFilter = (filter: any, tokenSuggestions: TokenSuggestion[]): any => {
  if (!filter) return filter;
  if (filter.and) return { and: { operands: filter.and.operands.map(o => serializeContentPermissionsFilter(o, tokenSuggestions)) } };
  if (filter.or) return { or: { operands: filter.or.operands.map(o => serializeContentPermissionsFilter(o, tokenSuggestions)) } };
  const inTokens = new Set(tokenSuggestions.filter(t => t.operator === 'in').map(t => t.value));
  const arrayCondition = filter.expression?.arrayCondition;
  const sc = arrayCondition ? arrayCondition.underlying?.stringCondition : filter.expression?.stringCondition;
  const inValue = sc?.in;
  if (typeof inValue !== 'string' || !inTokens.has(inValue)) return filter;
  const serializedUnderlying = { ...arrayCondition?.underlying, stringCondition: { in: { values: [inValue] } } };
  return {
    expression: arrayCondition
      ? { arrayCondition: { underlying: serializedUnderlying, resolution: arrayCondition.resolution } }
      : { ...filter.expression, stringCondition: { in: { values: [inValue] } } },
  };
};

export const useContentPermissionsTokenSuggestions = (): TokenSuggestion[] => {
  const { data } = useGqlQuery<ApiTokenSuggestion[]>({
    queryKey: [QueryKey.ContentPermissions, 'tokenSuggestions'],
    query: GET_CONTENT_PERMISSION_TOKEN_SUGGESTIONS,
  });

  return (data ?? []).map(mapApiTokenSuggestion);
};

const GET_PERMISSIONS = gql`
  query findPermissionSets {
    findPermissionSets {
      permissionSet {
        id
        name
        type
        filter
        accountId
        createdByUserId
        createdAt
        updatedAt
        updatedByUserId
      }
      users {
        id
        firstName
        lastName
      }
      permissionSetLinks {
        tags {
          tagId
          tagName
          sharedType
        }
        resources {
          resourceType
          resourceId
          sharedType
        }
      }
    }
  }
`;

export const useContentPermissions = (options: { refetchOnMount?: boolean | 'always'; enabled?: boolean } = {}) => {
  const { featureFlags } = useAvContext();
  const result = useGqlQuery<PermissionSetResponse[]>({
    queryKey: [QueryKey.ContentPermissions],
    query: GET_PERMISSIONS,
    ...options,
  });

  const data = useMemo(() => {
    const mapped = result.data?.map(({ permissionSet, users, permissionSetLinks }) => ({ ...permissionSet, users, permissionSetLinks }));
    if (!featureFlags[FeatureFlags.DashboardReportPermissionSetSharing])
      return mapped?.filter(ps => ps.type !== PermissionSetType.OLTPEntity);
    return mapped;
  }, [result.data, featureFlags[FeatureFlags.DashboardReportPermissionSetSharing]]);

  return { ...result, data };
};

const GET_TAGS = gql`
  query findTags {
    findTags {
      id
      name
      resources {
        resourceType
        resourceId
      }
      permissionSets {
        permissionSetId
        permissionSetName
        sharedType
      }
    }
  }
`;

export const useTags = (options: { enabled?: boolean } = {}) =>
  useGqlQuery<TagDto[]>({
    queryKey: [QueryKey.ContentPermissions, 'tags'],
    query: GET_TAGS,
    ...options,
  });
